/**
 * Analytics Settings Component
 * 
 * Displays email and reply analytics with customizable date ranges.
 * Uses on-demand data aggregation from existing Firestore collections.
 * Saves reports for later retrieval and download.
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { firestore } from '../firebaseConfig';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, setDoc, deleteDoc, updateDoc, deleteField, serverTimestamp } from 'firebase/firestore';
import SettingsTabNav from './SettingsTabNav';
import OutreachQualityCheck from './OutreachQualityCheck';
import ReplyInsights from './ReplyInsights';
import './AnalyticsSettings.css';

function ReplyMailText({ value, label, onChange }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      className="an-rr-card-text an-rr-card-edit an-rr-screen-only"
      value={value}
      aria-label={label}
      placeholder="Antwort hier kürzen oder ergänzen"
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

const AnalyticsSettings = ({ userId }) => {
  // Date range state
  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  
  // Report name state
  const [reportName, setReportName] = useState('');
  
  // Analytics data state
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Saved reports state
  const [savedReports, setSavedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState('');
  const [deletingReportId, setDeletingReportId] = useState(null);

  // Reply Report state — live list of positive replies for a period, one entry
  // per person (first positive message only), rendered as a print-friendly page
  // the operator can hand off to a manager. Separate from the aggregated
  // analytics report on purpose: this one is about individual replies, not
  // totals, and is intentionally NOT persisted.
  const [rrRange, setRrRange] = useState('last30');
  const [rrCustomStart, setRrCustomStart] = useState('');
  const [rrCustomEnd, setRrCustomEnd] = useState('');
  const [rrMinScore, setRrMinScore] = useState(6);
  const [rrLoading, setRrLoading] = useState(false);
  const [rrError, setRrError] = useState(null);
  const [rrData, setRrData] = useState(null);
  // Per-user "hide from report" state. Persisted to
  // `world_reply_report_prefs/{userId}.hiddenIds` so the operator's choices
  // survive across sessions. Held here as a Set for O(1) lookup while
  // rendering the list.
  const [rrHiddenIds, setRrHiddenIds] = useState(() => new Set());
  const [rrShowHidden, setRrShowHidden] = useState(false);
  const [rrBodyMode, setRrBodyMode] = useState('highlight');
  const [rrName, setRrName] = useState('');
  const [rrSavedReports, setRrSavedReports] = useState([]);
  const [rrSelectedId, setRrSelectedId] = useState('');
  const [rrLoadingSaved, setRrLoadingSaved] = useState(false);
  const [rrDeletingId, setRrDeletingId] = useState(null);
  const rrDataRef = useRef(null);
  const rrSelectedIdRef = useRef('');
  const rrTextSaveTimer = useRef(null);
  const [anPane, setAnPane] = useState('overview');

  // Auto Analytics state
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [autoSenderAccountId, setAutoSenderAccountId] = useState('');
  const [autoRecipients, setAutoRecipients] = useState([]);
  const [newRecipientEmail, setNewRecipientEmail] = useState('');
  const [emailAccounts, setEmailAccounts] = useState([]);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [autoTestSending, setAutoTestSending] = useState(false);
  const [autoSaveSuccess, setAutoSaveSuccess] = useState(false);
  const [autoHasUnsavedChanges, setAutoHasUnsavedChanges] = useState(false);
  const [autoLastSent, setAutoLastSent] = useState(null);
  const [autoError, setAutoError] = useState(null);

  // Reset auto analytics state and reload when userId changes
  useEffect(() => {
    if (userId) {
      setAutoEnabled(false);
      setAutoSenderAccountId('');
      setAutoRecipients([]);
      setAutoLastSent(null);
      setAutoError(null);
      setAutoHasUnsavedChanges(false);
      setRrHiddenIds(new Set());
      loadSavedReports();
      loadSavedReplyReports();
      loadAutoAnalyticsConfig();
      loadEmailAccounts();
      loadReplyReportPrefs();
    }
  }, [userId]);

  // Load persisted "hide from report" preferences. Missing doc → no hidden
  // items, no error surfaced (nothing to do). Team members with the inbox
  // feature can read/write via the rule added in firestore.rules.
  const loadReplyReportPrefs = async () => {
    if (!userId) return;
    try {
      const prefsRef = doc(firestore, 'world_reply_report_prefs', userId);
      const snap = await getDoc(prefsRef);
      if (snap.exists()) {
        const data = snap.data();
        setRrHiddenIds(new Set(Object.keys(data.hiddenIds || {})));
        if (data.bodyMode === 'full' || data.bodyMode === 'highlight') {
          setRrBodyMode(data.bodyMode);
        }
      }
    } catch (err) {
      console.warn('📮 [ReplyReport] prefs load failed:', err);
    }
  };

  // Toggle hide state for a single reply. Uses a dotted field path so we
  // touch only the single entry in the hiddenIds map — no risk of clobbering
  // concurrent writes from another tab.
  const toggleHideReply = async (replyId) => {
    if (!userId || !replyId) return;
    const next = new Set(rrHiddenIds);
    const wasHidden = next.has(replyId);
    if (wasHidden) next.delete(replyId); else next.add(replyId);
    setRrHiddenIds(next);

    const prefsRef = doc(firestore, 'world_reply_report_prefs', userId);
    try {
      if (wasHidden) {
        // Unhide → delete just this entry. `updateDoc` requires the doc to
        // exist; it always does here because we only unhide something we
        // previously hid.
        await updateDoc(prefsRef, { [`hiddenIds.${replyId}`]: deleteField() });
      } else {
        // Hide → merge the new entry in, creating the doc on first use.
        await setDoc(prefsRef, {
          hiddenIds: { [replyId]: { hiddenAt: serverTimestamp() } },
        }, { merge: true });
      }
    } catch (err) {
      console.error('📮 [ReplyReport] hide toggle failed:', err);
      // Revert local state so the button reflects reality.
      setRrHiddenIds(rrHiddenIds);
    }
  };

  const setReplyBodyMode = async (mode) => {
    if (mode !== 'highlight' && mode !== 'full') return;
    setRrBodyMode(mode);
    if (!userId) return;
    try {
      await setDoc(doc(firestore, 'world_reply_report_prefs', userId), { bodyMode: mode }, { merge: true });
    } catch (err) {
      console.warn('📮 [ReplyReport] bodyMode save failed:', err);
    }
  };

  // Load saved reports directly from Firestore
  const loadSavedReports = async () => {
    if (!userId) {
      console.log('📊 [Analytics] No userId, skipping load');
      return;
    }
    
    console.log('📊 [Analytics] Loading saved reports for user:', userId);
    setLoadingReports(true);
    try {
      const reportsRef = collection(firestore, 'world_analytics', userId, 'reports');
      const q = query(reportsRef, orderBy('createdAt', 'desc'), limit(20));
      const snapshot = await getDocs(q);
      
      console.log('📊 [Analytics] Found', snapshot.size, 'reports');
      
      const reports = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        console.log('📊 [Analytics] Report:', doc.id, data.periodLabel, data.name);
        reports.push({
          id: doc.id,
          name: data.name || null,
          periodStart: data.periodStart,
          periodEnd: data.periodEnd,
          periodLabel: data.periodLabel,
          emailsTotal: data.emails?.total || 0,
          repliesTotal: data.replies?.total || 0,
          replyRate: data.replyRate || 0,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || null,
          processingTimeMs: data.processingTimeMs
        });
      });
      
      setSavedReports(reports);
      console.log('📊 [Analytics] Reports loaded successfully:', reports.length);
    } catch (err) {
      console.error('📊 [Analytics] Error loading saved reports:', err);
    } finally {
      setLoadingReports(false);
    }
  };

  // Load email accounts from Firestore
  const loadEmailAccounts = async () => {
    if (!userId) return;
    try {
      const accountsRef = collection(firestore, 'world_users', userId, 'emailAccounts');
      const snapshot = await getDocs(accountsRef);
      const accounts = [];
      snapshot.forEach(d => {
        accounts.push({ id: d.id, email: d.data().email, sender_name: d.data().sender_name });
      });
      setEmailAccounts(accounts);
    } catch (err) {
      console.error('📊 [AutoAnalytics] Error loading email accounts:', err);
    }
  };

  // Load auto analytics config
  const loadAutoAnalyticsConfig = async () => {
    if (!userId) return;
    setAutoLoading(true);
    try {
      const configRef = doc(firestore, 'world_auto_analytics', userId);
      const configDoc = await getDoc(configRef);
      if (configDoc.exists()) {
        const data = configDoc.data();
        setAutoEnabled(data.enabled || false);
        setAutoSenderAccountId(data.senderAccountId || '');
        setAutoRecipients(data.recipientEmails || []);
        if (data.lastSentAt?.toDate) {
          setAutoLastSent({
            date: data.lastSentAt.toDate(),
            period: data.lastPeriod || null,
            emails: data.lastEmailsTotal || 0,
            replies: data.lastRepliesTotal || 0,
            rate: data.lastReplyRate || 0
          });
        }
        if (data.lastError) {
          setAutoError(data.lastError);
        }
      }
    } catch (err) {
      console.error('📊 [AutoAnalytics] Error loading config:', err);
    } finally {
      setAutoLoading(false);
    }
  };

  // Save auto analytics config
  const saveAutoAnalyticsConfig = async () => {
    if (!userId) return;

    if (autoEnabled && !autoSenderAccountId) {
      alert('Please select a sender email account.');
      return;
    }
    if (autoEnabled && autoRecipients.length === 0) {
      alert('Please add at least one recipient email.');
      return;
    }

    setAutoSaving(true);
    setAutoSaveSuccess(false);
    try {
      const configRef = doc(firestore, 'world_auto_analytics', userId);
      await setDoc(configRef, {
        enabled: autoEnabled,
        senderAccountId: autoSenderAccountId,
        recipientEmails: autoRecipients,
        updatedAt: new Date()
      }, { merge: true });
      setAutoSaveSuccess(true);
      setAutoHasUnsavedChanges(false);
      setTimeout(() => setAutoSaveSuccess(false), 3000);
    } catch (err) {
      console.error('📊 [AutoAnalytics] Error saving config:', err);
      alert('Failed to save auto analytics settings.');
    } finally {
      setAutoSaving(false);
    }
  };

  // Add recipient email
  const addRecipientEmail = () => {
    const email = newRecipientEmail.trim().toLowerCase();
    if (!email) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      alert('Please enter a valid email address.');
      return;
    }
    if (autoRecipients.includes(email)) {
      alert('This email is already in the list.');
      return;
    }
    setAutoRecipients(prev => [...prev, email]);
    setNewRecipientEmail('');
    setAutoHasUnsavedChanges(true);
  };

  // Remove recipient email
  const removeRecipientEmail = (email) => {
    setAutoRecipients(prev => prev.filter(e => e !== email));
    setAutoHasUnsavedChanges(true);
  };

  // Send test analytics email
  const sendTestAnalyticsEmail = async () => {
    if (!autoSenderAccountId || autoRecipients.length === 0) {
      alert('Please configure sender and recipients first, then save.');
      return;
    }
    setAutoTestSending(true);
    setAutoError(null);
    try {
      // Versendet eine echte Mail — läuft deshalb in Europa. getWorldAnalytics
      // weiter unten ist noch nicht umgezogen und bleibt auf der Default-Region.
      const functions = getFunctions(undefined, 'europe-west1');
      const trigger = httpsCallable(functions, 'triggerAutoAnalyticsEmail');
      const result = await trigger({ worldId: userId });
      setAutoError(null);
      alert(`Test email sent successfully! ${result.data.emailsSent} emails, ${result.data.repliesReceived} replies, ${result.data.replyRate}% reply rate.`);
      loadAutoAnalyticsConfig();
    } catch (err) {
      console.error('📊 [AutoAnalytics] Test send error:', err);
      setAutoError(err.message || 'Failed to send test email.');
    } finally {
      setAutoTestSending(false);
    }
  };

  // Load a specific saved report directly from Firestore
  const loadSavedReport = async (reportId) => {
    if (!userId || !reportId) return;
    
    setLoading(true);
    setError(null);
    setSelectedReportId(reportId);

    try {
      const reportRef = doc(firestore, 'world_analytics', userId, 'reports', reportId);
      const reportDoc = await getDoc(reportRef);
      
      if (!reportDoc.exists()) {
        throw new Error('Report not found');
      }
      
      const report = reportDoc.data();
      
      // Convert Firestore timestamp to ISO string
      if (report.createdAt?.toDate) {
        report.createdAt = report.createdAt.toDate().toISOString();
      }
      
      setAnalytics(report);
      console.log('📊 Saved report loaded:', report);
    } catch (err) {
      console.error('Error loading saved report:', err);
      setError(err.message || 'Failed to load saved report');
    } finally {
      setLoading(false);
    }
  };

  // Handle report dropdown change
  const handleReportSelect = (e) => {
    const reportId = e.target.value;
    if (reportId) {
      loadSavedReport(reportId);
    } else {
      setSelectedReportId('');
    }
  };

  // Delete a saved report directly from Firestore
  const deleteReport = async (reportId) => {
    if (!userId) return;
    if (!window.confirm('Are you sure you want to delete this report?')) {
      return;
    }

    setDeletingReportId(reportId);
    try {
      const reportRef = doc(firestore, 'world_analytics', userId, 'reports', reportId);
      await deleteDoc(reportRef);
      setSavedReports(prev => prev.filter(r => r.id !== reportId));
      
      // Clear selection and analytics if current report was deleted
      if (selectedReportId === reportId) {
        setSelectedReportId('');
        setAnalytics(null);
      }
    } catch (err) {
      console.error('Error deleting report:', err);
      alert('Failed to delete report');
    } finally {
      setDeletingReportId(null);
    }
  };

  // Calculate date range based on selection
  const getDateRange = useCallback(() => {
    const now = new Date();
    let start, end;
    
    switch (dateRange) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = now;
        break;
      case 'last7':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'last30':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'last90':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = now;
        break;
      case 'custom':
        if (customStartDate && customEndDate) {
          start = new Date(customStartDate);
          end = new Date(customEndDate);
        } else {
          return null;
        }
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = now;
    }
    
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0]
    };
  }, [dateRange, customStartDate, customEndDate]);

  // Load analytics data
  const loadAnalytics = async () => {
    const range = getDateRange();
    if (!range) {
      setError('Please select a valid date range');
      return;
    }

    setLoading(true);
    setError(null);
    setSelectedReportId(''); // Clear selection while generating

    try {
      const functions = getFunctions();
      const getWorldAnalytics = httpsCallable(functions, 'getWorldAnalytics');
      
      const result = await getWorldAnalytics({
        startDate: range.startDate,
        endDate: range.endDate,
        name: reportName.trim() || null,
        worldId: userId
      });

      setAnalytics(result.data);
      console.log('📊 Analytics loaded:', result.data);
      
      // Clear the report name input after successful generation
      setReportName('');
      
      // Set the new report as selected if reportId is returned
      if (result.data?.reportId) {
        setSelectedReportId(result.data.reportId);
      }
      
      // Refresh saved reports list
      loadSavedReports();
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  // Print report
  const printReport = () => {
    if (!analytics) return;
    window.print();
  };

  // ── Reply Report ────────────────────────────────────────────────────
  // Uses the same date-range primitives as the aggregate analytics report
  // above, but talks to a separate callable that returns the individual
  // qualifying replies (one per sender, first positive only).
  const getReplyRange = useCallback(() => {
    const now = new Date();
    let start;
    let end;
    switch (rrRange) {
      case 'last7':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'last30':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'last90':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        end = now;
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = now;
        break;
      case 'custom':
        if (rrCustomStart && rrCustomEnd) {
          start = new Date(rrCustomStart);
          end = new Date(rrCustomEnd);
        } else {
          return null;
        }
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = now;
    }
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  }, [rrRange, rrCustomStart, rrCustomEnd]);

  const replyReportsRef = () => collection(firestore, 'world_analytics', userId, 'reply_reports');

  useEffect(() => { rrDataRef.current = rrData; }, [rrData]);
  useEffect(() => { rrSelectedIdRef.current = rrSelectedId; }, [rrSelectedId]);
  useEffect(() => () => {
    if (rrTextSaveTimer.current) clearTimeout(rrTextSaveTimer.current);
  }, []);

  const updateReplyText = (id, text) => {
    setRrData((prev) => {
      if (!prev || !Array.isArray(prev.replies)) return prev;
      const next = {
        ...prev,
        replies: prev.replies.map((item) => (item.id === id ? { ...item, text } : item)),
      };
      rrDataRef.current = next;
      return next;
    });
    if (rrTextSaveTimer.current) clearTimeout(rrTextSaveTimer.current);
    const reportId = rrSelectedIdRef.current;
    rrTextSaveTimer.current = setTimeout(() => {
      const replies = rrDataRef.current?.replies;
      if (!userId || !reportId || rrSelectedIdRef.current !== reportId || !Array.isArray(replies)) return;
      updateDoc(doc(firestore, 'world_analytics', userId, 'reply_reports', reportId), { replies })
        .catch((err) => console.warn('📮 [ReplyReport] text save failed:', err));
    }, 400);
  };

  const loadSavedReplyReports = async () => {
    if (!userId) return;
    setRrLoadingSaved(true);
    try {
      const q = query(replyReportsRef(), orderBy('createdAt', 'desc'), limit(20));
      const snapshot = await getDocs(q);
      const reports = [];
      snapshot.forEach((d) => {
        const data = d.data();
        reports.push({
          id: d.id,
          name: data.name || null,
          periodStart: data.periodStart,
          periodEnd: data.periodEnd,
          periodLabel: data.periodLabel,
          totalReplies: data.totalReplies || 0,
          minScore: data.minScore,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || null,
        });
      });
      setRrSavedReports(reports);
    } catch (err) {
      console.warn('📮 [ReplyReport] saved list failed:', err);
    } finally {
      setRrLoadingSaved(false);
    }
  };

  const persistReplyReport = async (payload, name) => {
    if (!userId || !payload) return null;
    const reportId = `reply_${Date.now()}`;
    const periodStart = payload.period?.start || '';
    const periodEnd = payload.period?.end || '';
    await setDoc(doc(firestore, 'world_analytics', userId, 'reply_reports', reportId), {
      ...payload,
      id: reportId,
      kind: 'reply',
      name: name || null,
      periodStart,
      periodEnd,
      periodLabel: periodStart && periodEnd ? `${periodStart} – ${periodEnd}` : '',
      createdAt: serverTimestamp(),
    });
    return reportId;
  };

  const loadSavedReplyReport = async (reportId) => {
    if (!userId || !reportId) return;
    setRrLoading(true);
    setRrError(null);
    setRrSelectedId(reportId);
    try {
      const snap = await getDoc(doc(firestore, 'world_analytics', userId, 'reply_reports', reportId));
      if (!snap.exists()) throw new Error('Report nicht gefunden.');
      const data = snap.data();
      if (data.createdAt?.toDate) {
        data.createdAt = data.createdAt.toDate().toISOString();
      }
      setRrData(data);
    } catch (err) {
      console.error('📮 [ReplyReport] open saved failed:', err);
      setRrError(err.message || 'Gespeicherter Report konnte nicht geladen werden.');
    } finally {
      setRrLoading(false);
    }
  };

  const deleteSavedReplyReport = async (reportId) => {
    if (!userId || !reportId) return;
    if (!window.confirm('Diesen Reply Report wirklich löschen?')) return;
    setRrDeletingId(reportId);
    try {
      await deleteDoc(doc(firestore, 'world_analytics', userId, 'reply_reports', reportId));
      setRrSavedReports((prev) => prev.filter((r) => r.id !== reportId));
      if (rrSelectedId === reportId) {
        setRrSelectedId('');
        setRrData(null);
      }
    } catch (err) {
      console.error('📮 [ReplyReport] delete failed:', err);
      alert('Report konnte nicht gelöscht werden.');
    } finally {
      setRrDeletingId(null);
    }
  };

  const loadReplyReport = async () => {
    const range = getReplyRange();
    if (!range) {
      setRrError('Bitte gültigen Zeitraum wählen.');
      return;
    }
    setRrLoading(true);
    setRrError(null);
    setRrSelectedId('');
    try {
      const functions = getFunctions();
      const call = httpsCallable(functions, 'getReplyReport');
      const result = await call({
        startDate: range.startDate,
        endDate: range.endDate,
        minScore: rrMinScore,
        worldId: userId,
      });
      const payload = result.data;
      setRrData(payload);
      try {
        const reportId = await persistReplyReport(payload, rrName.trim() || null);
        if (reportId) {
          setRrSelectedId(reportId);
          setRrName('');
          loadSavedReplyReports();
        }
      } catch (saveErr) {
        console.warn('📮 [ReplyReport] save failed:', saveErr);
      }
    } catch (err) {
      console.error('📮 [ReplyReport] load error:', err);
      setRrError(err.message || 'Reply Report konnte nicht geladen werden.');
    } finally {
      setRrLoading(false);
    }
  };

  // Print only the reply report: we flip a body-level flag so print CSS can
  // hide the rest of the app, and clear it on `afterprint` (which fires even
  // when the user cancels the dialog). Falling back to a timeout keeps
  // Firefox happy where `afterprint` occasionally does not fire.
  const printReplyReport = () => {
    if (!rrData || visibleReplies.length === 0) return;
    document.body.setAttribute('data-an-print', 'reply-report');
    let cleared = false;
    const clear = () => {
      if (cleared) return;
      cleared = true;
      document.body.removeAttribute('data-an-print');
      window.removeEventListener('afterprint', clear);
    };
    window.addEventListener('afterprint', clear);
    setTimeout(clear, 60_000);
    // requestAnimationFrame → CSS is applied before the print dialog snapshots.
    requestAnimationFrame(() => window.print());
  };

  // Replies actually included in exports/print — respects the per-user hide
  // list. The visible on-screen list can additionally show hidden items when
  // `rrShowHidden` is true, but exports never do (that's the whole point).
  const replyTs = (r) => r?.timestamp || Date.parse(r?.date) || 0;

  const visibleReplies = useMemo(() => {
    if (!rrData || !Array.isArray(rrData.replies)) return [];
    return rrData.replies
      .filter((r) => !rrHiddenIds.has(r.id))
      .slice()
      .sort((a, b) => replyTs(a) - replyTs(b));
  }, [rrData, rrHiddenIds]);

  const displayedReplies = useMemo(() => {
    if (!rrData || !Array.isArray(rrData.replies)) return [];
    return rrData.replies
      .filter((r) => rrShowHidden || !rrHiddenIds.has(r.id))
      .slice()
      .sort((a, b) => replyTs(a) - replyTs(b));
  }, [rrData, rrHiddenIds, rrShowHidden]);

  const hiddenRepliesCount = useMemo(() => {
    if (!rrData || !Array.isArray(rrData.replies)) return 0;
    return rrData.replies.reduce((n, r) => n + (rrHiddenIds.has(r.id) ? 1 : 0), 0);
  }, [rrData, rrHiddenIds]);

  const rrStats = useMemo(() => {
    const scored = visibleReplies.filter((r) => typeof r.enthusiasmScore === 'number');
    const avg = scored.length
      ? scored.reduce((sum, r) => sum + r.enthusiasmScore, 0) / scored.length
      : null;
    return { avg };
  }, [visibleReplies]);

  const downloadReplyReportCsv = () => {
    if (!rrData || !Array.isArray(rrData.replies)) return;
    const esc = (val) => {
      const s = val == null ? '' : String(val);
      // Excel-safe: wrap and escape double quotes; strip newlines from single-cell fields.
      return `"${s.replace(/"/g, '""').replace(/\r?\n/g, ' ⏎ ')}"`;
    };
    const rows = [
      ['Datum', 'Name', 'E-Mail', 'Betreff', 'Empfangen auf', 'Score', 'Topic', 'Subtype', 'Weitere Antworten', 'Highlight', 'Text'],
      ...visibleReplies.map((r) => [
        new Date(r.date).toLocaleString('de-DE'),
        r.contactName || r.fromName || '',
        r.fromEmail || '',
        r.subject || '',
        r.accountEmail || '',
        r.enthusiasmScore ?? '',
        r.emailTopic || '',
        r.replySubtype || '',
        r.followUpCount || 0,
        r.highlight || '',
        r.text || '',
      ]),
    ];
    const csv = rows.map((row) => row.map(esc).join(',')).join('\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reply-report_${rrData.period?.start}_to_${rrData.period?.end}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const scoreBadgeClass = (score) => {
    if (score == null) return 'neutral';
    if (score >= 9) return 'strong';
    if (score >= 7) return 'good';
    if (score >= 6) return 'mid';
    return 'low';
  };

  // Thermometer-style labels ("heiss") match how sales teams already talk
  // about lead temperature — nicer on a printed page than a bare score.
  const scoreLabel = (score) => {
    if (score == null) return 'Ohne Score';
    if (score >= 9) return 'Sehr heiss';
    if (score >= 7) return 'Heiss';
    if (score >= 6) return 'Warm';
    if (score >= 4) return 'Neutral';
    return 'Kritisch';
  };

  const pad2 = (n) => String(n).padStart(2, '0');

  const formatRailDate = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return `${pad2(d.getDate())}.${pad2(d.getMonth() + 1)}.${String(d.getFullYear()).slice(-2)}`;
    } catch (_) {
      return '';
    }
  };

  const formatAvgScore = (avg) => {
    if (avg == null) return '—';
    return avg.toFixed(1).replace('.', ',');
  };

  const replyTitle = (r) => r.contactName || r.fromName || r.fromEmail || 'Unbekannt';

  const replyPersonLine = (r) => {
    const title = replyTitle(r);
    const person = r.fromName && r.fromName !== title ? r.fromName : '';
    return [person, r.fromEmail].filter(Boolean).join(' · ');
  };

  const rrPeriodLabel = {
    last7: 'Letzte 7 Tage',
    last30: 'Letzte 30 Tage',
    last90: 'Letzte 90 Tage',
    thisMonth: 'Diesen Monat',
    lastMonth: 'Letzten Monat',
    thisYear: 'Dieses Jahr',
    custom: 'Individuell',
  }[rrRange] || 'Letzte 30 Tage';

  // Download report as JSON
  const downloadJSON = () => {
    if (!analytics) return;
    
    const dataStr = JSON.stringify(analytics, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${analytics.period?.start}_to_${analytics.period?.end}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Download report as CSV
  const downloadCSV = () => {
    if (!analytics) return;

    let csv = 'Analytics Report\n';
    csv += `Period,${analytics.period?.start} to ${analytics.period?.end}\n`;
    csv += `Generated,${analytics.generatedAt || new Date().toISOString()}\n\n`;

    // Summary
    csv += 'SUMMARY\n';
    csv += `Total Emails Sent,${analytics.emails?.total || 0}\n`;
    csv += `Total Replies,${analytics.replies?.total || 0}\n`;
    csv += `Reply Rate,${analytics.replyRate || 0}%\n`;
    csv += `Avg Reply Sentiment,${analytics.replies?.enthusiasm?.avg || 0}\n\n`;

    // Emails by Type
    csv += 'EMAILS BY TYPE\n';
    csv += 'Type,Count\n';
    if (analytics.emails?.byType) {
      Object.entries(analytics.emails.byType).forEach(([type, count]) => {
        csv += `${type},${count}\n`;
      });
    }
    csv += '\n';

    // Emails by Day
    csv += 'EMAILS BY DAY\n';
    csv += 'Date,Count\n';
    if (analytics.emails?.byDay) {
      Object.entries(analytics.emails.byDay)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([date, count]) => {
          csv += `${date},${count}\n`;
        });
    }
    csv += '\n';

    // Replies by Day
    csv += 'REPLIES BY DAY\n';
    csv += 'Date,Count\n';
    if (analytics.replies?.byDay) {
      Object.entries(analytics.replies.byDay)
        .sort((a, b) => a[0].localeCompare(b[0]))
        .forEach(([date, count]) => {
          csv += `${date},${count}\n`;
        });
    }
    csv += '\n';

    // Top Topics
    csv += 'TOP REPLY TOPICS\n';
    csv += 'Topic,Count,Avg Enthusiasm\n';
    if (analytics.replies?.topTopics) {
      analytics.replies.topTopics.forEach(topic => {
        csv += `"${topic.topic}",${topic.count},${topic.avgEnthusiasm || 'N/A'}\n`;
      });
    }

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics_${analytics.period?.start}_to_${analytics.period?.end}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Format number with commas
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return num.toLocaleString();
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get enthusiasm label
  const getEnthusiasmLabel = (score) => {
    if (score >= 8) return { label: 'Excellent', class: 'excellent' };
    if (score >= 6) return { label: 'Good', class: 'good' };
    if (score >= 4) return { label: 'Neutral', class: 'neutral' };
    if (score >= 2) return { label: 'Low', class: 'low' };
    return { label: 'Very Low', class: 'very-low' };
  };

  // Get reply rate class
  const getReplyRateClass = (rate) => {
    if (rate >= 15) return 'excellent';
    if (rate >= 10) return 'good';
    if (rate >= 5) return 'neutral';
    return 'low';
  };

  // Render email type breakdown
  const renderEmailTypeBreakdown = () => {
    if (!analytics?.emails?.byType) return null;

    const typeLabels = {
      intro: 'Intro Emails',
      follow_up_1: 'First Follow-up',
      follow_up_2: 'Second Follow-up',
      follow_up_3: 'Third Follow-up',
      custom_reply: 'Custom Replies',
      email_reply: 'Email Replies',
      unknown: 'Other'
    };

    const types = Object.entries(analytics.emails.byType)
      .sort((a, b) => b[1] - a[1]);

    return (
      <div className="analytics_breakdown">
        {types.map(([type, count]) => (
          <div key={type} className="analytics_breakdown_item">
            <span className="analytics_breakdown_label">
              {typeLabels[type] || type}
            </span>
            <span className="analytics_breakdown_value">{formatNumber(count)}</span>
            <div className="analytics_breakdown_bar">
              <div 
                className="analytics_breakdown_fill"
                style={{ width: `${(count / analytics.emails.total) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render enthusiasm distribution
  const renderEnthusiasmDistribution = () => {
    if (!analytics?.replies?.enthusiasm?.distribution) return null;

    const distribution = analytics.replies.enthusiasm.distribution;
    const total = Object.values(distribution).reduce((a, b) => a + b, 0);

    const labels = {
      veryHigh: { label: 'Very Positive (9-10)', class: 'very_high' },
      high: { label: 'Positive (7-8)', class: 'high' },
      neutral: { label: 'Neutral (5-6)', class: 'neutral' },
      low: { label: 'Negative (3-4)', class: 'low' },
      veryLow: { label: 'Very Negative (1-2)', class: 'very_low' }
    };

    return (
      <div className="analytics_distribution">
        {['veryHigh', 'high', 'neutral', 'low', 'veryLow'].map(key => (
          <div key={key} className={`analytics_distribution_item ${labels[key].class}`}>
            <span className="analytics_distribution_label">{labels[key].label}</span>
            <span className="analytics_distribution_value">{distribution[key] || 0}</span>
            <div className="analytics_distribution_bar">
              <div 
                className={`analytics_distribution_fill ${labels[key].class}`}
                style={{ width: total > 0 ? `${((distribution[key] || 0) / total) * 100}%` : '0%' }}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Render top topics
  const renderTopTopics = () => {
    if (!analytics?.replies?.topTopics?.length) return null;

    return (
      <div className="analytics_topics">
        {analytics.replies.topTopics.map((topic, index) => (
          <div key={topic.topic} className="analytics_topic_item">
            <span className="analytics_topic_rank">#{index + 1}</span>
            <span className="analytics_topic_name">{topic.topic}</span>
            <span className="analytics_topic_count">{topic.count} replies</span>
            {topic.avgEnthusiasm > 0 && (
              <span className={`analytics_topic_score ${getEnthusiasmLabel(topic.avgEnthusiasm).class}`}>
                {topic.avgEnthusiasm.toFixed(1)}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  // Render sender breakdown
  const renderSenderBreakdown = () => {
    if (!analytics?.emails?.topSenders?.length) return null;

    return (
      <div className="analytics_senders">
        {analytics.emails.topSenders.map((sender, index) => (
          <div key={sender.email} className="analytics_sender_item">
            <span className="analytics_sender_rank">#{index + 1}</span>
            <span className="analytics_sender_email" title={sender.email}>
              {sender.label || sender.email}
            </span>
            <span className="analytics_sender_count">{formatNumber(sender.sent)}</span>
          </div>
        ))}
      </div>
    );
  };

  // Check if daily chart should be shown (for periods of 31 days or less)
  const shouldShowDailyChart = () => {
    // Show daily chart for any period of 31 days or less, based on actual report data
    const days = analytics?.period?.days;
    if (days && days <= 31) return true;
    return false;
  };

  // Render daily emails bar chart
  const renderDailyEmailsChart = () => {
    if (!analytics?.emails?.byDay || !shouldShowDailyChart()) return null;

    const byDay = analytics.emails.byDay;
    const days = Object.keys(byDay).sort();
    
    if (days.length === 0) return null;

    const maxCount = Math.max(...Object.values(byDay), 1);

    // Format day label (show short date)
    const formatDayLabel = (dateStr) => {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    return (
      <div className="analytics_daily_chart_section">
        <h4>Emails sent per day</h4>
        <div className="analytics_daily_chart">
          <div className="analytics_chart_bars">
            {days.map((day) => {
              const count = byDay[day] || 0;
              const heightPercent = (count / maxCount) * 100;
              
              return (
                <div key={day} className="analytics_chart_bar_container">
                  <div className="analytics_chart_bar_wrapper">
                    <span className="analytics_chart_bar_value">{count}</span>
                    <div 
                      className="analytics_chart_bar"
                      style={{ height: `${Math.max(heightPercent, 2)}%` }}
                      title={`${formatDayLabel(day)}: ${count} emails`}
                    />
                  </div>
                  <span className="analytics_chart_bar_label">{formatDayLabel(day)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const periodLabel = {
    today: 'Today', last7: 'Last 7 days', last30: 'Last 30 days', last90: 'Last 90 days',
    thisMonth: 'This month', lastMonth: 'Last month', thisYear: 'This year', custom: 'Custom range',
  }[dateRange] || 'Last 30 days';

  return (
    <div className="an analytics_container" data-print-date={new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}>
      <header className="an-page-head">
        <div>
          <h3 className="an-page-title">Analytics</h3>
          <p className="an-page-sub">
            {anPane === 'replies'
              ? 'Positive Rückmeldungen als druckbare Pipeline — eine Karte pro Person, ohne Verlauf.'
              : anPane === 'insights'
              ? 'Einwände, Feedback und unklare Stellen aus den letzten Antworten.'
              : anPane === 'quality'
              ? 'Up to 30 emails from the last 30 days, shuffled each time.'
              : 'Generate a report for a period, reopen a saved one, or send a monthly summary by email.'}
          </p>
        </div>
        {anPane === 'overview' && (
          <span className="an-badge">{savedReports.length} saved</span>
        )}
        {anPane === 'replies' && (
          <span className="an-badge">{rrSavedReports.length} gespeichert</span>
        )}
      </header>

      <SettingsTabNav
        tabs={[
          { id: 'overview', label: 'Report', icon: 'fa-chart-simple' },
          {
            id: 'replies',
            label: 'Reply Report',
            icon: 'fa-comments',
            count: rrData ? visibleReplies.length : 0,
          },
          { id: 'insights', label: 'Antwort-Analyse', icon: 'fa-lightbulb' },
          { id: 'quality', label: 'Quality Check', icon: 'fa-envelope-open-text' },
        ]}
        activeId={anPane}
        onChange={setAnPane}
        ariaLabel="Analytics methods"
      />

      {anPane === 'overview' && (
      <>
      <section className="an-card">
        <div className="an-status">
          <div className="an-status-lead">
            <span className={`an-status-dot ${analytics ? 'on' : 'off'}`} aria-hidden />
            <span>
              {analytics
                ? `Showing ${analytics.period?.start} – ${analytics.period?.end}${analytics.period?.days ? ` · ${analytics.period.days} days` : ''}.`
                : `Pick a period and generate a report. Default is ${periodLabel.toLowerCase()}. Reports are saved automatically.`}
            </span>
          </div>
        </div>

        <div className="an-controls">
          <div className="an-field">
            <label htmlFor="an-period">Period</label>
            <select
              id="an-period"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="an-input"
            >
              <option value="today">Today</option>
              <option value="last7">Last 7 days</option>
              <option value="last30">Last 30 days</option>
              <option value="last90">Last 90 days</option>
              <option value="thisMonth">This month</option>
              <option value="lastMonth">Last month</option>
              <option value="thisYear">This year</option>
              <option value="custom">Custom range</option>
            </select>
          </div>

          {dateRange === 'custom' && (
            <>
              <div className="an-field">
                <label htmlFor="an-from">From</label>
                <input id="an-from" type="date" className="an-input" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} />
              </div>
              <div className="an-field">
                <label htmlFor="an-to">To</label>
                <input id="an-to" type="date" className="an-input" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} />
              </div>
            </>
          )}

          <div className="an-field an-field--grow">
            <label htmlFor="an-name">Report name</label>
            <input
              id="an-name"
              type="text"
              className="an-input"
              value={reportName}
              onChange={(e) => setReportName(e.target.value)}
              placeholder="Optional, e.g. Q4 campaign"
              maxLength={50}
            />
          </div>

          <button type="button" className="altBtn altBtn--save" onClick={loadAnalytics} disabled={loading}>
            {loading ? 'Generating…' : 'Generate report'}
          </button>
        </div>

        <div className="an-saved">
          <label htmlFor="an-saved">Saved reports</label>
          <div className="an-saved-row">
            <select
              id="an-saved"
              className="an-input"
              value={selectedReportId}
              onChange={handleReportSelect}
              disabled={loading || loadingReports}
            >
              {loadingReports ? (
                <option value="">Loading…</option>
              ) : savedReports.length === 0 ? (
                <option value="">None yet — generate one above</option>
              ) : (
                <>
                  <option value="">Open a saved report</option>
                  {savedReports.map(report => (
                    <option key={report.id} value={report.id}>
                      {report.name ? `${report.name} · ${report.periodLabel}` : report.periodLabel} · {report.emailsTotal} emails · {report.replyRate}%
                    </option>
                  ))}
                </>
              )}
            </select>
            {selectedReportId && (
              <button
                type="button"
                className="altBtn altBtn--petrol"
                onClick={() => deleteReport(selectedReportId)}
                disabled={deletingReportId === selectedReportId}
              >
                {deletingReportId === selectedReportId ? '…' : 'Delete'}
              </button>
            )}
          </div>
        </div>
      </section>

        {error && (
          <div className="an-error">{error}</div>
        )}

        {analytics && (
          <section className="an-card analytics_results">
            <div className="an-period">
              <span>
                {analytics.period?.start} – {analytics.period?.end}
                {analytics.period?.days ? ` · ${analytics.period.days} days` : ''}
              </span>
              <div className="an-period-actions">
                <button type="button" className="altBtn altBtn--petrol" onClick={printReport}>Print</button>
                <button type="button" className="altBtn altBtn--petrol" onClick={downloadCSV}>CSV</button>
                <button type="button" className="altBtn altBtn--petrol" onClick={downloadJSON}>JSON</button>
              </div>
            </div>

            <div className="an-metrics">
              <div className="an-metric">
                <span className="an-metric-value">{formatNumber(analytics.emails?.total)}</span>
                <span className="an-metric-label">Emails sent</span>
                {analytics.emails?.dailyAverage && (
                  <span className="an-metric-sub">~{analytics.emails.dailyAverage} / day</span>
                )}
              </div>
              <div className="an-metric">
                <span className="an-metric-value">{formatNumber(analytics.replies?.total)}</span>
                <span className="an-metric-label">Replies</span>
                {analytics.replies?.dailyAverage && (
                  <span className="an-metric-sub">~{analytics.replies.dailyAverage} / day</span>
                )}
              </div>
              <div className="an-metric">
                <span className="an-metric-value">{analytics.replyRate}%</span>
                <span className="an-metric-label">Reply rate</span>
                <span className="an-metric-sub">
                  {analytics.replyRate >= 10 ? 'Strong' : analytics.replyRate >= 5 ? 'Solid' : 'Room to grow'}
                </span>
              </div>
              <div className="an-metric">
                <span className="an-metric-value">{analytics.replies?.enthusiasm?.avg || 0}</span>
                <span className="an-metric-label">Reply sentiment</span>
                <span className="an-metric-sub">{getEnthusiasmLabel(analytics.replies?.enthusiasm?.avg || 0).label}</span>
              </div>
            </div>

            {/* Daily Emails Chart */}
            {renderDailyEmailsChart()}

            {/* Detailed Sections */}
            <div className="analytics_sections">
              {/* Email Breakdown */}
              <div className="analytics_section">
                <h4>Emails by type</h4>
                {renderEmailTypeBreakdown()}
              </div>

              {/* Sender Breakdown - Hidden for now */}
              {/* SafeGuard Stats - Hidden for now */}

              {/* Reply Sentiment Distribution */}
              {analytics.replies?.total > 0 && (
                <div className="analytics_section">
                  <h4>Reply sentiment</h4>
                  {renderEnthusiasmDistribution()}
                </div>
              )}

              {/* Top Reply Topics */}
              {analytics.replies?.topTopics?.length > 0 && (
                <div className="analytics_section">
                  <h4>Top reply topics</h4>
                  {renderTopTopics()}
                </div>
              )}
            </div>
          </section>
        )}

        {!analytics && !loading && !error && (
          <div className="an-empty">
            Choose a period above and generate a report. It is saved automatically so you can open it again later.
          </div>
        )}

      <section className="an-card">
        <div className="an-bookings-head">
          <div>
            <h4 className="an-section-title">Monthly email</h4>
            <p className="an-section-sub">A summary on the 1st of every month, sent from one of your accounts.</p>
          </div>
        </div>

        {autoLoading ? (
          <div className="an-empty">Loading…</div>
        ) : (
          <>
            <div className="an-row">
              <div className="an-row-copy">
                <strong>Send a monthly report</strong>
                <span>On the first of the month we email the last period’s numbers.</span>
              </div>
              <button
                type="button"
                className={`an-toggle ${autoEnabled ? 'on' : ''}`}
                onClick={() => { setAutoEnabled(!autoEnabled); setAutoHasUnsavedChanges(true); }}
                aria-pressed={autoEnabled}
              >
                <span className="an-toggle-knob" />
              </button>
            </div>

            {autoEnabled && (
              <div className="an-auto-config">
                <div className="an-field">
                  <label htmlFor="an-from-account">Send from</label>
                  <select
                    id="an-from-account"
                    className="an-input"
                    value={autoSenderAccountId}
                    onChange={(e) => { setAutoSenderAccountId(e.target.value); setAutoHasUnsavedChanges(true); }}
                  >
                    <option value="">Choose a sender account</option>
                    {emailAccounts.map(acc => (
                      <option key={acc.id} value={acc.id}>
                        {acc.sender_name ? `${acc.sender_name} (${acc.email})` : acc.email}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="an-field an-field--stack">
                  <label htmlFor="an-to-email">Send to</label>
                  <div className="an-saved-row">
                    <input
                      id="an-to-email"
                      type="email"
                      className="an-input"
                      placeholder="Add a recipient"
                      value={newRecipientEmail}
                      onChange={(e) => setNewRecipientEmail(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addRecipientEmail(); } }}
                    />
                    <button
                      type="button"
                      className="altBtn altBtn--petrol"
                      onClick={addRecipientEmail}
                      disabled={!newRecipientEmail.trim()}
                    >
                      Add
                    </button>
                  </div>
                  {autoRecipients.length > 0 && (
                    <div className="an-tags">
                      {autoRecipients.map(email => (
                        <button
                          key={email}
                          type="button"
                          className="an-tag"
                          onClick={() => removeRecipientEmail(email)}
                          title="Remove"
                        >
                          {email}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {autoLastSent && (
                  <p className="an-note">
                    Last sent {autoLastSent.period} — {autoLastSent.emails} emails, {autoLastSent.replies} replies, {autoLastSent.rate}% reply rate.
                  </p>
                )}

                {autoError && <div className="an-error">{autoError}</div>}

                <div className="an-actions">
                  <button
                    type="button"
                    className="altBtn altBtn--petrol"
                    onClick={sendTestAnalyticsEmail}
                    disabled={autoTestSending || !autoSenderAccountId || autoRecipients.length === 0 || autoHasUnsavedChanges}
                  >
                    {autoTestSending ? 'Sending…' : 'Send test'}
                  </button>
                  <button
                    type="button"
                    className="altBtn altBtn--save"
                    onClick={saveAutoAnalyticsConfig}
                    disabled={autoSaving}
                  >
                    {autoSaving ? 'Saving…' : autoSaveSuccess ? 'Saved' : 'Save'}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </section>
      </>
      )}

      {anPane === 'replies' && (
      <section className="an-card an-rr">
        <div className="an-bookings-head">
          <div>
            <h4 className="an-section-title">Reply Report</h4>
            <p className="an-section-sub">
              Alle positiven Rückmeldungen in einem Zeitraum — je eine Karte pro Person,
              ohne zitierten Verlauf. Zum Drucken oder Weiterleiten an Team &amp; GF.
            </p>
          </div>
          {rrData && (
            <span className="an-badge">{rrData.totalReplies} {rrData.totalReplies === 1 ? 'Antwort' : 'Antworten'}</span>
          )}
        </div>

        <div className="an-controls">
          <div className="an-field">
            <label htmlFor="an-rr-period">Zeitraum</label>
            <select
              id="an-rr-period"
              className="an-input"
              value={rrRange}
              onChange={(e) => setRrRange(e.target.value)}
            >
              <option value="last7">Letzte 7 Tage</option>
              <option value="last30">Letzte 30 Tage</option>
              <option value="last90">Letzte 90 Tage</option>
              <option value="thisMonth">Diesen Monat</option>
              <option value="lastMonth">Letzten Monat</option>
              <option value="thisYear">Dieses Jahr</option>
              <option value="custom">Individueller Zeitraum</option>
            </select>
          </div>

          {rrRange === 'custom' && (
            <>
              <div className="an-field">
                <label htmlFor="an-rr-from">Von</label>
                <input
                  id="an-rr-from"
                  type="date"
                  className="an-input"
                  value={rrCustomStart}
                  onChange={(e) => setRrCustomStart(e.target.value)}
                />
              </div>
              <div className="an-field">
                <label htmlFor="an-rr-to">Bis</label>
                <input
                  id="an-rr-to"
                  type="date"
                  className="an-input"
                  value={rrCustomEnd}
                  onChange={(e) => setRrCustomEnd(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="an-field">
            <span className="an-rr-seg-label" id="an-rr-body-label">Inhalt</span>
            <div className="an-rr-seg" role="tablist" aria-labelledby="an-rr-body-label">
              <button
                type="button"
                role="tab"
                aria-selected={rrBodyMode === 'highlight'}
                className={`an-rr-seg-btn ${rrBodyMode === 'highlight' ? 'is-on' : ''}`}
                onClick={() => setReplyBodyMode('highlight')}
              >
                Highlights
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={rrBodyMode === 'full'}
                className={`an-rr-seg-btn ${rrBodyMode === 'full' ? 'is-on' : ''}`}
                onClick={() => setReplyBodyMode('full')}
              >
                Komplette Mails
              </button>
            </div>
          </div>

          <div className="an-field an-field--grow">
            <label htmlFor="an-rr-name">Name</label>
            <input
              id="an-rr-name"
              type="text"
              className="an-input"
              value={rrName}
              onChange={(e) => setRrName(e.target.value)}
              placeholder="Optional, z.B. September Pipeline"
              maxLength={50}
            />
          </div>

          <div className="an-field">
            <label htmlFor="an-rr-score">Ab Score</label>
            <select
              id="an-rr-score"
              className="an-input"
              value={rrMinScore}
              onChange={(e) => setRrMinScore(Number(e.target.value))}
            >
              <option value={6}>6+  ·  Positiv (Standard)</option>
              <option value={7}>7+  ·  Klar interessiert</option>
              <option value={8}>8+  ·  Sehr positiv</option>
              <option value={9}>9+  ·  Begeistert</option>
              <option value={1}>Alle Antworten</option>
            </select>
          </div>

          <button
            type="button"
            className="altBtn altBtn--save"
            onClick={loadReplyReport}
            disabled={rrLoading}
          >
            {rrLoading ? 'Lade…' : 'Report erstellen'}
          </button>
        </div>

        <div className="an-saved">
          <label htmlFor="an-rr-saved">Gespeicherte Reports</label>
          <div className="an-saved-row">
            <select
              id="an-rr-saved"
              className="an-input"
              value={rrSelectedId}
              onChange={(e) => {
                const id = e.target.value;
                if (id) loadSavedReplyReport(id);
                else setRrSelectedId('');
              }}
              disabled={rrLoading || rrLoadingSaved}
            >
              {rrLoadingSaved ? (
                <option value="">Lade…</option>
              ) : rrSavedReports.length === 0 ? (
                <option value="">Noch keiner — oben erstellen</option>
              ) : (
                <>
                  <option value="">Gespeicherten Report öffnen</option>
                  {rrSavedReports.map((report) => (
                    <option key={report.id} value={report.id}>
                      {report.name ? `${report.name} · ` : ''}
                      {report.periodLabel}
                      {` · ${report.totalReplies} ${report.totalReplies === 1 ? 'Antwort' : 'Antworten'}`}
                    </option>
                  ))}
                </>
              )}
            </select>
            {rrSelectedId && (
              <button
                type="button"
                className="altBtn altBtn--petrol"
                onClick={() => deleteSavedReplyReport(rrSelectedId)}
                disabled={rrDeletingId === rrSelectedId}
              >
                {rrDeletingId === rrSelectedId ? '…' : 'Löschen'}
              </button>
            )}
          </div>
        </div>

        {rrError && <div className="an-error">{rrError}</div>}

        {rrData && (
          <>
            <div className="an-rr-toolbar">
              <div className="an-rr-toolbar-copy">
                {visibleReplies.length} {visibleReplies.length === 1 ? 'positive Rückmeldung' : 'positive Rückmeldungen'}
                {hiddenRepliesCount > 0 && (
                  <span className="an-rr-summary-hidden"> · {hiddenRepliesCount} ausgeblendet</span>
                )}
              </div>
              <div className="an-rr-summary-actions">
                <button type="button" className="altBtn altBtn--petrol" onClick={printReplyReport} disabled={visibleReplies.length === 0}>
                  Drucken / PDF
                </button>
                <button type="button" className="altBtn altBtn--petrol" onClick={downloadReplyReportCsv} disabled={visibleReplies.length === 0}>
                  CSV
                </button>
              </div>
            </div>

            {rrData.truncated && (
              <div className="an-note">
                Es wurden nur die ersten {rrData.truncatedAt} Antworten geladen — bitte den Zeitraum eingrenzen.
              </div>
            )}

            {hiddenRepliesCount > 0 && (
              <button
                type="button"
                className="an-rr-toggle-hidden"
                onClick={() => setRrShowHidden((v) => !v)}
              >
                {rrShowHidden
                  ? `Ausgeblendete verstecken`
                  : `${hiddenRepliesCount} ausgeblendete ${hiddenRepliesCount === 1 ? 'Antwort' : 'Antworten'} zeigen`}
              </button>
            )}

            {rrData.totalReplies === 0 ? (
              <div className="an-empty">
                Keine passenden Antworten in diesem Zeitraum. Score senken oder Zeitraum erweitern?
              </div>
            ) : (
              <div className="an-rr-pipe">
                <header className="an-rr-pipe-head">
                  <div className="an-rr-pipe-kicker">Reply Report · {rrPeriodLabel}</div>
                  <h3 className="an-rr-pipe-title">Lead-Pipeline</h3>
                  <p className="an-rr-pipe-lead">
                    {visibleReplies.length} {visibleReplies.length === 1 ? 'Rückmeldung' : 'Rückmeldungen'}, chronologisch nach Eingang sortiert.
                  </p>
                </header>

                <div className="an-rr-kpis">
                  <div className="an-rr-kpi">
                    <span className="an-rr-kpi-value">{visibleReplies.length}</span>
                    <span className="an-rr-kpi-label">{visibleReplies.length === 1 ? 'Rückmeldung' : 'Rückmeldungen'}</span>
                  </div>
                  <div className="an-rr-kpi">
                    <span className="an-rr-kpi-value">
                      {rrStats.avg != null ? `${formatAvgScore(rrStats.avg)} von 10` : '—'}
                    </span>
                    <span className="an-rr-kpi-label">Positivity Score</span>
                  </div>
                </div>

                <div className="an-rr-legend">
                  <span className="an-rr-legend-title">Rating:</span>
                  <span className="an-rr-legend-item"><i className="an-rr-dot an-rr-dot--strong" /> Sehr heiss (9–10)</span>
                  <span className="an-rr-legend-item"><i className="an-rr-dot an-rr-dot--good" /> Heiss (7–8)</span>
                  <span className="an-rr-legend-item"><i className="an-rr-dot an-rr-dot--mid" /> Warm (6)</span>
                </div>

                <ol className="an-rr-tl">
                  {displayedReplies.map((r) => {
                    const isHidden = rrHiddenIds.has(r.id);
                    const heat = scoreBadgeClass(r.enthusiasmScore);
                    const printIndex = visibleReplies.findIndex((v) => v.id === r.id);
                    return (
                      <li
                        key={r.id}
                        className={`an-rr-tl-item an-rr-tl-item--${heat}${isHidden ? ' an-rr-tl-item--hidden' : ''}`}
                      >
                        <div className="an-rr-tl-rail">
                          <span className="an-rr-tl-idx">{printIndex >= 0 ? pad2(printIndex + 1) : '—'}</span>
                          <span className="an-rr-tl-date">{formatRailDate(r.date)}</span>
                        </div>
                        <div className="an-rr-tl-axis" aria-hidden="true">
                          <span className={`an-rr-tl-dot an-rr-dot an-rr-dot--${heat}`} />
                        </div>
                        <article className="an-rr-card">
                          <div className="an-rr-card-head">
                            <div className="an-rr-card-who">
                              <h4 className="an-rr-card-name">{replyTitle(r)}</h4>
                              {replyPersonLine(r) && (
                                <p className="an-rr-card-meta">{replyPersonLine(r)}</p>
                              )}
                            </div>
                            <div className="an-rr-card-tools">
                              {r.enthusiasmScore != null && (
                                <span className={`an-rr-badge an-rr-badge--${heat}`}>
                                  <span className="an-rr-badge-score">{r.enthusiasmScore}/10</span>
                                  {scoreLabel(r.enthusiasmScore)}
                                </span>
                              )}
                              <button
                                type="button"
                                className={`an-rr-hide-btn an-rr-screen-only ${isHidden ? 'an-rr-hide-btn--on' : ''}`}
                                onClick={() => toggleHideReply(r.id)}
                                title={isHidden ? 'Wieder in Report aufnehmen' : 'Aus Report ausblenden'}
                                aria-label={isHidden ? 'Wieder anzeigen' : 'Ausblenden'}
                              >
                                <i className={`fa ${isHidden ? 'fa-eye-slash' : 'fa-eye'}`} aria-hidden="true" />
                                <span className="an-rr-hide-label">{isHidden ? 'Ausgeblendet' : 'Ausblenden'}</span>
                              </button>
                            </div>
                          </div>
                          {r.subject && (
                            <div className="an-rr-card-subject">Betreff: {r.subject}</div>
                          )}
                          {rrBodyMode === 'full' ? (
                            <>
                              <ReplyMailText
                                value={r.text || ''}
                                label={`Text von ${replyTitle(r)} bearbeiten`}
                                onChange={(text) => updateReplyText(r.id, text)}
                              />
                              {r.text ? (
                                <div className="an-rr-card-text an-rr-print-only">{r.text}</div>
                              ) : (
                                <div className="an-rr-card-text an-rr-card-text--empty an-rr-print-only">
                                  Kein Text verfügbar — die Mail hat keinen Plaintext-Body gespeichert.
                                </div>
                              )}
                            </>
                          ) : r.highlight ? (
                            <blockquote className="an-rr-card-quote">„{r.highlight}“</blockquote>
                          ) : (
                            <div className="an-rr-card-text an-rr-card-text--empty">
                              Kein Highlight in dieser Mail.
                            </div>
                          )}
                          {r.followUpCount > 0 && (
                            <div className="an-rr-card-foot">
                              + {r.followUpCount} weitere {r.followUpCount === 1 ? 'Antwort' : 'Antworten'}
                            </div>
                          )}
                          {r.accountEmail && (
                            <div className="an-rr-card-foot an-rr-screen-only">
                              Empfangen auf {r.accountEmail}
                            </div>
                          )}
                        </article>
                      </li>
                    );
                  })}
                </ol>
                <p className="an-rr-pipe-mark">bonusclub</p>
              </div>
            )}
          </>
        )}

        {!rrData && !rrLoading && !rrError && (
          <div className="an-empty">
            Zeitraum wählen und „Report erstellen“ klicken. Standard: alle Antworten mit Rating 6 oder höher aus den letzten {rrPeriodLabel.toLowerCase()}.
          </div>
        )}
      </section>
      )}

      {anPane === 'insights' && <ReplyInsights userId={userId} />}

      {anPane === 'quality' && <OutreachQualityCheck userId={userId} />}
    </div>
  );
};

export default AnalyticsSettings;
